import type { LexicalEditor } from 'lexical';

/**
 * 编辑器内 InlineBox 节点的运行时视觉态（选中/加载中）。
 *
 * 重要：这两类状态**不**保存在 LexicalNode 上，而是放在该模块级 store。
 * 原因：若挂在节点上，每次切换都需要 `getWritable()` 触发 commit，会被 lexical-history
 * 当作有效改动 push 一条历史，撤销时会"先撤销选中态"。把视觉态外挂到 store 后，
 * selection 变化只触发 React 订阅刷新，不进入 EditorState，不参与 undo/redo。
 *
 * 隔离：用 WeakMap<editor, bucket> 按编辑器实例隔离，避免多个 InputTextArea
 * 之间 nodeKey 重复带来的串台。
 */
export interface InlineBoxRuntimeState {
  selected: boolean;
  loading: boolean;
}

const DEFAULT_STATE: InlineBoxRuntimeState = { selected: false, loading: false };

type Listener = (state: InlineBoxRuntimeState) => void;

interface EditorBucket {
  states: Map<string, InlineBoxRuntimeState>;
  listeners: Map<string, Set<Listener>>;
}

const buckets = new WeakMap<LexicalEditor, EditorBucket>();

const getBucket = (editor: LexicalEditor): EditorBucket => {
  let bucket = buckets.get(editor);
  if (!bucket) {
    bucket = { states: new Map(), listeners: new Map() };
    buckets.set(editor, bucket);
  }
  return bucket;
};

const notify = (bucket: EditorBucket, key: string, next: InlineBoxRuntimeState) => {
  const listeners = bucket.listeners.get(key);
  if (!listeners) return;
  listeners.forEach((listener) => listener(next));
};

export const getInlineBoxRuntimeState = (
  editor: LexicalEditor | null | undefined,
  nodeKey: string,
): InlineBoxRuntimeState => {
  if (!editor) return DEFAULT_STATE;
  const bucket = buckets.get(editor);
  return bucket?.states.get(nodeKey) ?? DEFAULT_STATE;
};

export const subscribeInlineBoxRuntimeState = (
  editor: LexicalEditor | null | undefined,
  nodeKey: string,
  listener: Listener,
): (() => void) => {
  if (!editor) return () => {};
  const bucket = getBucket(editor);
  let listeners = bucket.listeners.get(nodeKey);
  if (!listeners) {
    listeners = new Set();
    bucket.listeners.set(nodeKey, listeners);
  }
  listeners.add(listener);
  return () => {
    const set = bucket.listeners.get(nodeKey);
    set?.delete(listener);
    if (set && set.size === 0) bucket.listeners.delete(nodeKey);
  };
};

const writeState = (
  bucket: EditorBucket,
  nodeKey: string,
  patch: Partial<InlineBoxRuntimeState>,
) => {
  const prev = bucket.states.get(nodeKey) ?? DEFAULT_STATE;
  const next: InlineBoxRuntimeState = { ...prev, ...patch };
  if (prev.selected === next.selected && prev.loading === next.loading) return;
  bucket.states.set(nodeKey, next);
  notify(bucket, nodeKey, next);
};

export const setInlineBoxLoading = (
  editor: LexicalEditor,
  nodeKey: string,
  loading: boolean,
) => writeState(getBucket(editor), nodeKey, { loading });

export const clearInlineBoxRuntimeState = (
  editor: LexicalEditor,
  nodeKey: string,
) => {
  const bucket = buckets.get(editor);
  if (!bucket || !bucket.states.has(nodeKey)) return;
  bucket.states.delete(nodeKey);
  notify(bucket, nodeKey, DEFAULT_STATE);
};

/**
 * 批量同步当前应该处于 selected 状态的 nodeKey 集合：
 * - 不在集合内但当前是 selected 的全部置 false；
 * - 在集合内但当前是 false 的置 true。
 * loading 字段不动。
 */
export const syncInlineBoxSelectedKeys = (
  editor: LexicalEditor,
  selectedKeys: Set<string>,
) => {
  const bucket = getBucket(editor);
  const seen = new Set<string>();
  bucket.states.forEach((state, key) => {
    seen.add(key);
    const shouldSelect = selectedKeys.has(key);
    if (state.selected === shouldSelect) return;
    writeState(bucket, key, { selected: shouldSelect });
  });
  selectedKeys.forEach((key) => {
    if (seen.has(key)) return;
    writeState(bucket, key, { selected: true });
  });
};
